import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  GpsFixed as GpsIcon,
  Check as CheckIcon,
  Place as PlaceIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

// Helper function to check if Google Maps is loaded
const isGoogleMapsLoaded = () => {
  return window.google && window.google.maps;
};

const DeliveryLocationSelector = ({
  onLocationConfirm,
  initialAddress = '',
  initialLocation = null,
  initialCity = '',
  initialState = '',
  initialPincode = '',
  initialManualAddress = '',
  height = 400,
  placeholder = "Enter your delivery address...",
  label = "Delivery Address"
}) => {
  // Component state
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [selectedAddress, setSelectedAddress] = useState(initialAddress);
  const [placeId, setPlaceId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Address form fields - initialize with props
  const [city, setCity] = useState(initialCity);
  const [state, setState] = useState(initialState);
  const [pincode, setPincode] = useState(initialPincode);
  const [manualAddress, setManualAddress] = useState(initialManualAddress);

  // Global variables for Google Maps
  const [mapInstance, setMapInstance] = useState(null);
  const [markerInstance, setMarkerInstance] = useState(null);
  const [geocoderInstance, setGeocoderInstance] = useState(null);
  const [autocompleteInstance, setAutocompleteInstance] = useState(null);

  // Refs
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const componentId = useRef(`delivery-selector-${Date.now()}`);

  // Initialize Google Maps (simplified - map only, no Street View)
  const initMap = useCallback(() => {
    try {
      const mapElement = mapRef.current;
      const inputElement = autocompleteRef.current;

      if (!mapElement || !inputElement) return;

      // Initialize Geocoder for reverse geocoding
      const geocoderInstance = new window.google.maps.Geocoder();
      setGeocoderInstance(geocoderInstance);

      // Initialize the map
      const mapInstance = new window.google.maps.Map(mapElement, {
        center: selectedLocation || { lat: 20.5937, lng: 78.9629 }, // Center on India
        zoom: selectedLocation ? 18 : 5,
        mapTypeControl: true,
        streetViewControl: true, // Enable built-in Street View control
        fullscreenControl: true,
        zoomControl: true,
      });

      // Create a draggable marker
      const markerInstance = new window.google.maps.Marker({
        map: mapInstance,
        position: selectedLocation || mapInstance.getCenter(),
        draggable: true,
        animation: window.google.maps.Animation.DROP,
        title: "Drag to set precise delivery location",
      });

      // Update location info when marker is dragged
      markerInstance.addListener("dragend", () => {
        const latLng = markerInstance.getPosition();
        const location = {
          lat: latLng.lat(),
          lng: latLng.lng(),
        };
        updateLocation(location, markerInstance, mapInstance, geocoderInstance);
      });

      // Map click listener
      mapInstance.addListener('click', (event) => {
        const clickedLocation = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };
        updateLocation(clickedLocation, markerInstance, mapInstance, geocoderInstance);
      });

      // Initialize Places Autocomplete
      const autocompleteInstance = new window.google.maps.places.Autocomplete(inputElement, {
        types: ["address"], // Restrict to address predictions
        componentRestrictions: { country: 'in' }, // Restrict to India
        fields: ['formatted_address', 'geometry', 'place_id', 'address_components', 'name']
      });

      // Autocomplete event listener
      autocompleteInstance.addListener('place_changed', () => {
        const place = autocompleteInstance.getPlace();

        if (!place.geometry || !place.geometry.location) {
          // User entered invalid input or Place Details request failed
          console.warn("No details available for input: '" + (place.name || 'unknown') + "'");
          return;
        }

        // Extract address components
        let city = '', state = '', pincode = '';
        if (place.address_components) {
          place.address_components.forEach(component => {
            if (component.types.includes('locality') || component.types.includes('administrative_area_level_2')) {
              city = component.long_name;
            }
            if (component.types.includes('administrative_area_level_1')) {
              state = component.long_name;
            }
            if (component.types.includes('postal_code')) {
              pincode = component.long_name;
            }
          });
        }

        // If the place has a geometry, present it on the map
        const location = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        setSelectedLocation(location);
        setSelectedAddress(place.formatted_address);
        setPlaceId(place.place_id);
        setCity(city);
        setState(state);
        setPincode(pincode);

        // Update map view
        if (place.geometry.viewport) {
          mapInstance.fitBounds(place.geometry.viewport);
        } else {
          mapInstance.setCenter(place.geometry.location);
          mapInstance.setZoom(18);
        }

        // Move marker to selected location
        markerInstance.setPosition(place.geometry.location);

        // Store the Place ID
        console.log("Selected Place ID:", place.place_id);
        console.log("Extracted: City:", city, "State:", state, "Pincode:", pincode);
      });

      setMapInstance(mapInstance);
      setMarkerInstance(markerInstance);
      setAutocompleteInstance(autocompleteInstance);
      setLoading(false);

    } catch (error) {
      console.error('Error initializing delivery location selector:', error);
      setApiError(true);
      setLoading(false);
    }
  }, [selectedLocation, onLocationConfirm]);

  // Update location and perform reverse geocoding
  const updateLocation = async (location, markerInstance, mapInstance, geocoderInstance) => {
    setSelectedLocation(location);
    markerInstance.setPosition(location);
    mapInstance.setCenter(location);

    // Perform reverse geocoding to get address and populate form fields
    try {
      const response = await geocoderInstance.geocode({ location: location });
      if (response.results && response.results[0]) {
        const address = response.results[0].formatted_address;
        setSelectedAddress(address);
        setPlaceId(response.results[0].place_id);

        // Extract city, state, pincode from reverse geocoding result
        let newCity = '', newState = '', newPincode = '';
        if (response.results[0].address_components) {
          response.results[0].address_components.forEach(component => {
            if (component.types.includes('locality') || component.types.includes('administrative_area_level_2')) {
              newCity = component.long_name;
            }
            if (component.types.includes('administrative_area_level_1')) {
              newState = component.long_name;
            }
            if (component.types.includes('postal_code')) {
              newPincode = component.long_name;
            }
          });
        }

        // Update form fields with reverse geocoded data
        setCity(newCity);
        setState(newState);
        setPincode(newPincode);

        console.log('Reverse geocoded - City:', newCity, 'State:', newState, 'Pincode:', newPincode);
      }
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      // Keep existing address if geocoding fails
    }
  };


  // Get current location using GPS
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const currentLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        setSelectedLocation(currentLocation);

        if (mapInstance && markerInstance && geocoderInstance) {
          await updateLocation(currentLocation, markerInstance, mapInstance, geocoderInstance);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to get your current location. Please select location manually.');
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  };

  // Confirm location selection
  const confirmLocation = () => {
    if (selectedLocation && selectedAddress) {
      const locationData = {
        address: selectedAddress,
        location: selectedLocation,
        placeId: placeId,
        city: city,
        state: state,
        pincode: pincode,
        manualAddress: manualAddress,
        confirmed: true,
        timestamp: new Date().toISOString()
      };

      if (onLocationConfirm) {
        onLocationConfirm(locationData);
      }

      setShowConfirmDialog(false);
    }
  };

  // Load Google Maps API (similar to your vanilla JS approach)
  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      initMap();
      return;
    }

    // Create global callback function (similar to your vanilla JS example)
    const callbackName = `initMap_${componentId.current}`;
    window[callbackName] = () => {
      initMap();
      // Clean up the global callback
      delete window[callbackName];
    };

    // Load Google Maps API script (similar to your vanilla JS example)
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    if (!apiKey) {
      console.error('Google Maps API key not found in environment variables');
      setApiError(true);
      setLoading(false);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geocoding&callback=${callbackName}`;
    script.async = true;
    script.defer = true;

    script.onerror = () => {
      console.error('Failed to load Google Maps API');
      setApiError(true);
      setLoading(false);
    };

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (window[callbackName]) {
        delete window[callbackName];
      }
    };
  }, [initMap]);

  // Update input field when selectedAddress changes
  useEffect(() => {
    if (autocompleteRef.current && selectedAddress && selectedAddress !== autocompleteRef.current.value) {
      autocompleteRef.current.value = selectedAddress;
    }
  }, [selectedAddress]);

  if (apiError) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'error.light' }}>
        <LocationIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
        <Typography variant="h6" color="error.main" gutterBottom>
          Google Maps Unavailable
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please enter your delivery address manually below.
        </Typography>

        <TextField
          fullWidth
          label="Delivery Address (Manual Entry)"
          placeholder="Enter your full delivery address"
          value={selectedAddress}
          onChange={(e) => setSelectedAddress(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Latitude"
              type="number"
              value={selectedLocation?.lat || ''}
              onChange={(e) => setSelectedLocation(prev => ({
                ...prev,
                lat: parseFloat(e.target.value) || 0
              }))}
              placeholder="e.g., 28.6139"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Longitude"
              type="number"
              value={selectedLocation?.lng || ''}
              onChange={(e) => setSelectedLocation(prev => ({
                ...prev,
                lng: parseFloat(e.target.value) || 0
              }))}
              placeholder="e.g., 77.2090"
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          onClick={() => setShowConfirmDialog(true)}
          disabled={!selectedAddress.trim()}
          startIcon={<CheckIcon />}
        >
          Confirm Manual Location
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Map Container with Embedded Search */}
      <Paper sx={{ position: 'relative', overflow: 'hidden', mb: 3, borderRadius: 2 }}>
        {/* Search Field Embedded in Map */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Paper
            sx={{
              p: '2px 4px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: 1,
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              bgcolor: 'white',
            }}
          >
            <PlaceIcon sx={{ p: '10px', color: 'text.secondary' }} />
            <TextField
              inputRef={autocompleteRef}
              fullWidth
              placeholder="Search for a location..."
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { px: 1 }
              }}
              sx={{
                '& .MuiInput-root': {
                  '&:before, &:after': {
                    display: 'none'
                  }
                }
              }}
            />
            <Tooltip title="Use Current Location">
              <IconButton
                onClick={getCurrentLocation}
                disabled={loading}
                sx={{ p: '10px', color: 'primary.main' }}
              >
                <GpsIcon />
              </IconButton>
            </Tooltip>
          </Paper>
        </Box>

        <Box
          ref={mapRef}
          sx={{
            width: '100%',
            height: height,
          }}
        />

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 1,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Loading Map...
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Address Details Form - Single Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Address Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="City"
              value={city}
              onChange={async (e) => {
                const newCity = e.target.value;
                setCity(newCity);

                // If user enters a city name, geocode it to find location
                if (newCity.trim().length > 2) {
                  try {
                    const geocoder = new window.google.maps.Geocoder();
                    const response = await geocoder.geocode({
                      address: newCity,
                      componentRestrictions: { country: 'IN' }
                    });

                    if (response.results && response.results[0]) {
                      const location = response.results[0].geometry.location;
                      const latLng = {
                        lat: location.lat(),
                        lng: location.lng(),
                      };

                      setSelectedLocation(latLng);
                      setSelectedAddress(response.results[0].formatted_address);

                      // Extract state and pincode from geocoded result
                      let newState = '', newPincode = '';
                      if (response.results[0].address_components) {
                        response.results[0].address_components.forEach(component => {
                          if (component.types.includes('administrative_area_level_1')) {
                            newState = component.long_name;
                          }
                          if (component.types.includes('postal_code')) {
                            newPincode = component.long_name;
                          }
                        });
                      }

                      setState(newState);
                      setPincode(newPincode);

                      // Update map
                      if (mapInstance && markerInstance) {
                        mapInstance.setCenter(latLng);
                        mapInstance.setZoom(12); // City level zoom
                        markerInstance.setPosition(latLng);
                      }

                      console.log('City geocoded - State:', newState, 'Pincode:', newPincode);
                    }
                  } catch (error) {
                    console.warn('City geocoding failed:', error);
                  }
                }
              }}
              variant="outlined"
              size="small"
              helperText="Type city name to auto-locate on map"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              variant="outlined"
              size="small"
              helperText="Auto-filled from map selection"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Pincode"
              value={pincode}
              onChange={async (e) => {
                const newPincode = e.target.value;
                setPincode(newPincode);

                // If user enters a valid pincode, geocode it to get location
                if (newPincode.length === 6 && /^\d+$/.test(newPincode)) {
                  try {
                    const geocoder = new window.google.maps.Geocoder();
                    const response = await geocoder.geocode({
                      address: newPincode,
                      componentRestrictions: { country: 'IN' }
                    });

                    if (response.results && response.results[0]) {
                      const location = response.results[0].geometry.location;
                      const latLng = {
                        lat: location.lat(),
                        lng: location.lng(),
                      };

                      setSelectedLocation(latLng);
                      setSelectedAddress(response.results[0].formatted_address);

                      // Extract city and state from geocoded result
                      let newCity = '', newState = '';
                      if (response.results[0].address_components) {
                        response.results[0].address_components.forEach(component => {
                          if (component.types.includes('locality') || component.types.includes('administrative_area_level_2')) {
                            newCity = component.long_name;
                          }
                          if (component.types.includes('administrative_area_level_1')) {
                            newState = component.long_name;
                          }
                        });
                      }

                      setCity(newCity);
                      setState(newState);

                      // Update map
                      if (mapInstance && markerInstance) {
                        mapInstance.setCenter(latLng);
                        mapInstance.setZoom(15);
                        markerInstance.setPosition(latLng);
                      }
                    }
                  } catch (error) {
                    console.warn('Pincode geocoding failed:', error);
                  }
                }
              }}
              variant="outlined"
              size="small"
              helperText="Enter pincode to auto-locate on map"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="House/Shop Address"
              placeholder="Flat 101, Building Name"
              value={manualAddress}
              onChange={(e) => setManualAddress(e.target.value)}
              variant="outlined"
              size="small"
              helperText="Enter house/shop details"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Instructions */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        📍 Search locations above or enter pincode below to auto-locate on map. Drag marker to adjust exact position.
      </Typography>

      {/* Confirm Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => setShowConfirmDialog(true)}
          disabled={!selectedLocation || !city.trim() || !manualAddress.trim() || loading}
          startIcon={<CheckIcon />}
          sx={{ px: 4, py: 1.5 }}
        >
          Confirm Address
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckIcon sx={{ mr: 1 }} />
          Confirm Delivery Address
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Please confirm your delivery address:
          </Typography>

          <Box sx={{ my: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              📍 {manualAddress}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {city}, {state} {pincode}
            </Typography>
            {selectedLocation && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Location: {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
              </Typography>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            This address will be used for delivery. Make sure all details are correct.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>Edit Address</Button>
          <Button onClick={confirmLocation} variant="contained">
            Confirm Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryLocationSelector;